import { IconUserGroup, toast } from '@gmg/gmg-react-components';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AppContext, { Role } from 'src/AppContext';
import { ModuleId } from 'src/graphql/mappers/module';
import { useCreateOrEditUser } from 'src/graphql/useCreateOrEditUser';
import { useInviteUser } from 'src/graphql/useInviteUser';
import { ModuleLicense } from 'src/viewModels/Organization';
import { User } from 'src/viewModels/User';
import { TableContainer } from '../../shared/TableStyle';
import OrganizationContainer from '../OrganizationContainer';
import SearchBox from '../shared/SearchBox';
import { getErrorMsgForToast } from '../toastErrorHandler';
import { UserModalState } from './AddOrEditUserModal';
import { CPGORole } from './ColorProofGo/cpGo';
import InviteUserBtn from './InviteUserBtn';
import UserManagementTable from './UserManagementTable';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';

export interface UserManagementProps {
    onUserUpdateAfterSelfEditing: () => void;
}

export interface LicenseSystemAccessSettings {
    assignedLicenseSystems: Array<string> | undefined;
    role: CPGORole | undefined;
}

export interface AddOrEditUserSubmitData {
    email: string;
    firstName: string;
    lastName: string;
    modules: Array<ModuleId>;
    id: string | undefined;
    role: Role;
    groups: Array<string>;
    cpGo: LicenseSystemAccessSettings;
    colorCard: LicenseSystemAccessSettings;
}

const UserManagement: FC<UserManagementProps> = (props) => {
    const { t } = useTranslation();
    const { user: currentUser } = useContext(AppContext);
    const { orgId } = useParams();
    const [users, setUsers] = useState<User[] | undefined>(undefined);
    const [groups, setGroups] = useState<Map<string, string>>(new Map());
    const [maintainableRoles, setMaintainableRoles] = useState<Role[]>([]);
    const [availableModules, setAvailableModules] = useState<Map<ModuleId, ModuleLicense>>(new Map());
    const [availableLicenseSystems, setAvailableLicenseSystems] = useState<LicenseSystem[]>([]);
    const [filterValue, setFilterValue] = useState<string>('');

    const editUser = useCreateOrEditUser();
    const inviteUser = useInviteUser();

    const handleInviteUser = (
        userData: AddOrEditUserSubmitData,
        setModalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => {
        setModalState({ isLoading: true, errMsg: undefined });

        const handleSuccess = (result: 'success' | 'user_already_invited') => {

            switch (result) {
                case 'success':
                    onClose?.();
                    toast.success(t('UserManagement.ToastMsg.toast_Addsucces_desc', 'An invitation has been sent.'));
                    break;
                case 'user_already_invited':
                    setModalState({
                        isLoading: false,
                        errMsg: t('UserManagement.Errors.emailAlreadyAssigned_error_desc', 'This email address is already assigned to an organization.'),
                    });
                    break;
                default:
                    break;
            }
        };

        inviteUser.mutate({
            email: userData.email.trim(),
            firstName: userData.firstName,
            lastName: userData.lastName,
            orgId: orgId!,
            role: userData.role,
            modules: userData.modules,
            groups: userData.groups,
            cpGo: {
                assignedLicenseSystems: userData.cpGo.assignedLicenseSystems,
                role: userData.cpGo.role,
            },
            colorCard: {
                assignedLicenseSystems: userData.colorCard.assignedLicenseSystems,
                role: userData.colorCard.role,
            },
        }, {
            onSuccess: handleSuccess,
            onError: response => {
                onClose?.();
                toast.error(getErrorMsgForToast(response, t));
            },
        });
    };

    const handleEditUser = (
        userData: AddOrEditUserSubmitData,
        setModalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => {
        setModalState({ isLoading: true, errMsg: undefined });

        const isUserSelfEditing = currentUser?.userId === userData.id;

        const handleSuccess = () => {
            // We want to refresh the information based on the user token if the user edits himself, i.e. assigns module licenses to himself.
            // Note that the user might still not be able to use the license in the target application without logging in/out in the target application.
            if (isUserSelfEditing) {
                props.onUserUpdateAfterSelfEditing();
            }
            toast.success(t('UserManagement.ToastMsg.toast_Editsucces_desc', 'Your changes have been successfully applied.'));
        };

        editUser.mutate({
            id: userData.id!,
            email: userData.email.trim(),
            firstName: userData.firstName,
            lastName: userData.lastName,
            orgId: orgId!,
            role: userData.role,
            modules: userData.modules,
            groups: userData.groups,
            cpGo: {
                assignedLicenseSystems: userData.cpGo.assignedLicenseSystems,
                role: userData.cpGo.role,
            },
            colorCard: {
                assignedLicenseSystems: userData.colorCard.assignedLicenseSystems,
                role: userData.colorCard.role,
            },
        }, {
            onSuccess: handleSuccess,
            onError: response => {
                toast.error(getErrorMsgForToast(response, t));
            },
        });

        onClose?.();
    };

    return (
        <OrganizationContainer
            button={
                <InviteUserBtn
                    orgId={orgId!}
                    maintainableRoles={maintainableRoles}
                    availableGroups={groups}
                    availableModules={availableModules}
                    onHandleInviteUser={handleInviteUser}
                    availableLicenseSystems={availableLicenseSystems}
                />
            }
            onOrganizationLoad={(organization, maintainableRoleList) => {
                setUsers(organization.users);
                setGroups(new Map(organization.groups.map(({ name, id }) => ([id, name]))));
                setMaintainableRoles(maintainableRoleList);
                setAvailableModules(organization.moduleLicenses.modules);
                setAvailableLicenseSystems(organization.licenseSystems);
            }}
            viewInfo={{
                icon: <IconUserGroup width={16} height={16} />,
                label: t('HeaderPageTitles.user_management_title', 'User Management'),
            }}
            searchBar={
                <SearchBox
                    onChange={(e) => setFilterValue(e.target.value)}
                    value={filterValue}
                />
            }
        >
            <TableContainer>
                {!users
                    ? <></>
                    // Quick fix not to see the spinner 'cos at that point we have already fetched the data
                    // and there is actually no loading state but react needs a tiny amount of time to update the ui
                    // React.suspense should be introduced with fallback UI
                    : (
                        <UserManagementTable
                            users={users}
                            filterValue={filterValue}
                            orgId={orgId!}
                            maintainableRoles={maintainableRoles}
                            availableGroups={groups}
                            availableModules={availableModules}
                            onHandleEditUser={handleEditUser}
                            availableLicenseSystems={availableLicenseSystems}
                        />
                    )
                }
            </TableContainer>
        </OrganizationContainer>
    );
};

export default UserManagement;